/**
 * The layer-0 map (WORKBOARD I7, owner §12.1): a fixed, north-up, bird's-eye topographic map of the
 * area of operations — not today's rotating planet view — with what the viewer's team knows
 * projected on it. At k_human = 1/20 a person sees a dozen heights before the planet's curve hides
 * everything (§13), so the map is where the battle's shape lives.
 *
 * The base is drawn once (and again when a crater changes the ground), a few rows a frame: the
 * planet's own colours, hillshaded from the north-west, contour lines every few person-heights with
 * every fifth heavier. Per frame, cheaply, on top: capture points (owner's colour, capture fill,
 * letter), your team's towers and emplacements, your team's units (you ringed, with a heading tick),
 * enemies only as your team knows them — the same fading dots and diamonds as the world's markers
 * (rendering/layer0Views.ts `confidenceOf`) — your drones and their routes, a north arrow and a
 * scale bar in person-heights.
 *
 * Projection: azimuthal equidistant about the area's centre, so map distance is walking distance.
 * The maths is pure and exported (tests/topoMap.test.ts). Presentation only: reads state, writes none.
 *
 * The view (owner, Oct 9: "some way to interact and expand the map"): a window over the projection —
 * a centre and a half-width in map coordinates — that pans and zooms without moving the projection.
 * The drone tablet pans (drag) and zooms (wheel, keys); the corner map only zooms (− / =) and keeps
 * you at its centre at every zoom (owner, Oct 10): its drawn window follows you each frame, while the
 * ground is sampled over a wider window (`sampleWindow`) and re-sampled only once you have drifted
 * far. While a moved window's terrain is sampled, the last one is drawn shifted and scaled under it,
 * so the map never blanks. Both views draw the same overlays: the corner map is the
 * tablet's data, smaller.
 */
import { PHYS_DT } from '../constants';
import { weatherHere, type WeatherKind } from '../core/weather';
import { cellsAt, weatherTime } from '../core/windField';
import { arrivalFrom, AHEAD_S, windAtPerson, windCue, type Arrival } from './groundHudView';
import type { GameState, PlanetState, Vec3 } from '../types';
import { TEAMS, G } from '../constants';
import { getSurfaceRadius, nearestSurfaceVertex } from '../core/surface';
import { computePlanetVertexColors } from '../core/planetColors';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { dronePos } from '../core/drones';
import { unitPaletteFor } from '../rendering/unitPalette';
import { drawTrackMarker, markerStep, type TrackMarkerKind } from '../rendering/trackMarker';
import { teamPicture } from '../core/picture';
import { forecastSeenBy } from '../core/installations';
import { dropshipMarks } from './dropshipMarks';
import { drawIcon } from './commandIcons';

// ---------------------------------------------------------------------------
// Projection (pure)
// ---------------------------------------------------------------------------

/** The map's frame on its planet: a centre, north and east at the centre, the planet's radius, and the arc from the centre to the map's edge. */
export interface TopoFrame { planetIndex: number; centre: Vec3; north: Vec3; east: Vec3; radius: number; extent: number }

const dot = (a: Vec3, b: Vec3) => a.x * b.x + a.y * b.y + a.z * b.z;
const norm = (a: Vec3): Vec3 => { const l = Math.hypot(a.x, a.y, a.z) || 1; return { x: a.x / l, y: a.y / l, z: a.z / l }; };
const cross = (a: Vec3, b: Vec3): Vec3 => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });

/**
 * Frame the area holding `dirs` (unit directions on the planet): centred on their mean, north
 * toward the planet's +Y (or +Z near the poles), the extent their farthest arc × `margin`, at least `minExtent`.
 */
export function topoFrame(planetIndex: number, radius: number, dirs: readonly Vec3[], minExtent: number, margin = 1.25): TopoFrame {
  let c = { x: 0, y: 0, z: 0 };
  for (const d of dirs) { const n = norm(d); c = { x: c.x + n.x, y: c.y + n.y, z: c.z + n.z }; }
  const centre = dirs.length && Math.hypot(c.x, c.y, c.z) > 1e-9 ? norm(c) : { x: 0, y: 1, z: 0 };
  const ref = Math.abs(centre.y) < 0.95 ? { x: 0, y: 1, z: 0 } : { x: 0, y: 0, z: 1 };
  const north = norm({ x: ref.x - centre.x * dot(ref, centre), y: ref.y - centre.y * dot(ref, centre), z: ref.z - centre.z * dot(ref, centre) });
  const east = cross(north, centre);                                    // seen from outside, north up → east to the right
  let far = 0;
  for (const d of dirs) far = Math.max(far, Math.acos(Math.max(-1, Math.min(1, dot(norm(d), centre)))) * radius);
  return { planetIndex, centre, north, east, radius, extent: Math.min(radius * Math.PI * 0.5, Math.max(minExtent, far * margin)) };
}

/** A direction → map coordinates (east, north) in arc length from the centre. */
export function toMap(f: TopoFrame, dir: Vec3): { x: number; y: number } {
  const d = norm(dir);
  const rho = Math.acos(Math.max(-1, Math.min(1, dot(d, f.centre)))) * f.radius;
  const e = dot(d, f.east), n = dot(d, f.north), h = Math.hypot(e, n);
  return h < 1e-12 ? { x: 0, y: 0 } : { x: (rho * e) / h, y: (rho * n) / h };
}

/** Map coordinates (east, north; arc length) → a direction. */
export function fromMap(f: TopoFrame, x: number, y: number): Vec3 {
  const rho = Math.hypot(x, y) / f.radius;
  if (rho < 1e-12) return { ...f.centre };
  const ux = x / Math.hypot(x, y), uy = y / Math.hypot(x, y);
  const s = Math.sin(rho), c = Math.cos(rho);
  return norm({
    x: f.centre.x * c + (f.east.x * ux + f.north.x * uy) * s,
    y: f.centre.y * c + (f.east.y * ux + f.north.y * uy) * s,
    z: f.centre.z * c + (f.east.z * ux + f.north.z * uy) * s,
  });
}

/**
 * 69.57 (DECISIONS 266, the owner: "the wind indicator, I think we should put that in the minimap … want to remove middle
 * wind chip thing"): the wind where you stand (`wind`, the same gusting wind your shots fly through, groundHudView
 * windAtPerson) on the north-up map — `bearing` the way it blows, degrees clockwise from the map's north, rounded to 5°;
 * `pips` its strength in five (a storm's push full, groundHudView windCue), at least one for any wind not calm (DECISIONS 268
 * (6), the owner: "I dont't see the minimap wind" — a light wind rounded to none and drew nothing); `words` calm · light ·
 * fresh · strong · a gale.
 * Rounded as the old chip was, so a steady wind costs no redraw and a gust moves it in steps. Null with no wind. Pure.
 */
export function windOnMap(f: TopoFrame, at: Vec3, wind: Vec3 | null, gravity: number): { bearing: number; pips: number; words: string } | null {
  if (!wind) return null;
  const cue = windCue(wind, at, f.north, gravity);
  if (cue.words === 'calm') return { bearing: 0, pips: 0, words: 'calm' };
  const pips = Math.max(1, Math.round(cue.strength * 5));
  const n = norm(at), a = dot(wind, n), h = norm({ x: wind.x - n.x * a, y: wind.y - n.y * a, z: wind.z - n.z * a });
  const p0 = toMap(f, n), p1 = toMap(f, norm({ x: n.x + h.x * 1e-4, y: n.y + h.y * 1e-4, z: n.z + h.z * 1e-4 }));
  const deg = ((Math.atan2(p1.x - p0.x, p1.y - p0.y) * 180) / Math.PI + 360) % 360;
  return { bearing: (Math.round(deg / 5) * 5) % 360, pips, words: cue.words };
}

/** 69.57: how the corner map shows the wind — `rim`: an arrow on the map's edge on the side it comes from, pointing the way it blows, as long as it is strong, its word beside; `corner`: a chip under the north mark, an arrow turned to it, five pips and the word. */
export type WindMark = 'rim' | 'corner';

/** A readable contour step for a height range: 1, 2 or 5 × 10ⁿ person-heights, about a dozen lines across the range. */
export function contourInterval(range: number, person: number): number {
  const raw = Math.max(range, person) / 12 / person;
  const p = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 5, 10].find(m => m * p >= raw)! * p;
  return Math.max(0.5, step) * person;
}

/** A window over a frame's projection: its centre (x east, y north) and half-width, in arc length. */
export interface TopoView { x: number; y: number; extent: number }

/** Keep a view's half-width within [min, max]. */
export function clampView(v: TopoView, min: number, max: number): TopoView {
  return { x: v.x, y: v.y, extent: Math.max(min, Math.min(max, v.extent)) };
}

/** Zoom by `factor` (< 1 in, > 1 out) keeping the map point under pixel (px, py) of a `size`-pixel map where it is. */
export function zoomAbout(v: TopoView, factor: number, px: number, py: number, size: number, min: number, max: number): TopoView {
  const s0 = size / (2 * v.extent);
  const mx = v.x + (px - size / 2) / s0, my = v.y - (py - size / 2) / s0;
  const extent = Math.max(min, Math.min(max, v.extent * factor)), s1 = size / (2 * extent);
  return { x: mx - (px - size / 2) / s1, y: my + (py - size / 2) / s1, extent };
}

/** Drag the map by (dx, dy) pixels: the ground under the cursor follows it. */
export function panView(v: TopoView, dx: number, dy: number, size: number): TopoView {
  const s = size / (2 * v.extent);
  return { x: v.x - dx / s, y: v.y + dy / s, extent: v.extent };
}

/** Map coordinates → pixels, and back, through a view. */
export function viewToPx(v: TopoView, size: number, m: { x: number; y: number }): { x: number; y: number } {
  const s = size / (2 * v.extent);
  return { x: size / 2 + (m.x - v.x) * s, y: size / 2 - (m.y - v.y) * s };
}
export function pxToView(v: TopoView, size: number, px: number, py: number): { x: number; y: number } {
  const s = size / (2 * v.extent);
  return { x: v.x + (px - size / 2) / s, y: v.y - (py - size / 2) / s };
}

/** The point `dist` along a route of unit directions on a sphere of `radius` (its end if the route is shorter). */
export function alongRoute(route: readonly Vec3[], radius: number, dist: number): Vec3 {
  let left = dist;
  for (let i = 0; i + 1 < route.length; i++) {
    const a = norm(route[i]), b = norm(route[i + 1]);
    const ang = Math.acos(Math.max(-1, Math.min(1, dot(a, b)))), len = ang * radius;
    if (left <= len && ang > 1e-12) {
      const t = left / len, sa = Math.sin(ang);
      const wa = Math.sin((1 - t) * ang) / sa, wb = Math.sin(t * ang) / sa;
      return norm({ x: a.x * wa + b.x * wb, y: a.y * wa + b.y * wb, z: a.z * wa + b.z * wb });
    }
    left -= len;
  }
  return norm(route[route.length - 1]);
}

/** Corner-map zoom levels: the view's half-width as a share of the area of operations. */
export const CORNER_ZOOMS = [1, 0.5, 0.25];
/** The corner map opens at ×2 (the owner, Oct 12 late: "minimap is too zoomed out by default, probably want to start with at least 2x current zoom, let's just run with 2x for now"); − still steps out to ×1. */
export const CORNER_ZOOM_START = 1;
/** The corner map samples the ground over a window this much wider than the drawn one, so the drawn window can follow you between re-samples. */
export const CORNER_MARGIN = 0.5;
/**
 * The window to sample the ground over for a drawn `view` that follows the viewer: `sampled` is kept
 * while the drawn window still lies inside it (the drift within the margin, the zoom unchanged);
 * otherwise a new one, centred on the view and wider by the margin. Pure.
 */
export function sampleWindow(view: TopoView, sampled: TopoView | null, margin: number): TopoView {
  const extent = view.extent * (1 + margin);
  if (sampled && sampled.extent === extent && Math.hypot(view.x - sampled.x, view.y - sampled.y) <= margin * view.extent) return sampled;
  return { x: view.x, y: view.y, extent };
}

/** A scale bar about a quarter of the map: a round number of person-heights. */
export function scaleBar(extent: number, person: number): number {
  const target = (extent * 2) / 4 / person;
  const p = Math.pow(10, Math.floor(Math.log10(target)));
  return [1, 2, 5].reduce((best, m) => (Math.abs(m * p - target) < Math.abs(best - target) ? m * p : best), p);
}

// ---------------------------------------------------------------------------
// The map
// ---------------------------------------------------------------------------

const BUDGET_MS = 4;

export class TopoMap {
  /** X16: the site's record, drawn in the corner when the host gives one. */
  private notes: readonly string[] | null = null;
  /** 62.19 (d): the planet's weather now and next (null without weather). */
  private forecast: WeatherKind[] | null = null;
  /** The owner, Oct 13 ("weather forecast is different across minimap and main middle hud … just put it all in minimap"): the weather where the viewer stands and, with weather cells, the front arriving there (north-up bearing). */
  private here: WeatherKind | null = null;
  private arrival: Arrival | null = null;
  /** 69.57: the wind where the viewer stands, on the map (the corner map, with the match's wind on). */
  private wind: ReturnType<typeof windOnMap> = null;
  /** 69.57: how the wind is drawn (the owner's pick between the two; `rim` until then). */
  windMark: WindMark = 'rim';
  readonly el: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly base: HTMLCanvasElement;
  private frame: TopoFrame | null = null;
  private view: TopoView | null = null;
  /** the view the drawn base was sampled for */
  private baseView: TopoView | null = null;
  /** the window the ground is sampled over: the corner map's is wider than its drawn view (sampleWindow); the tablet's is its view */
  private sampleView: TopoView | null = null;
  /** the HUD's corner map (follows you) rather than the tablet's (pans) */
  private readonly corner: boolean;
  private builtFor: PlanetState['heightmap'] | null = null;
  private job: { planet: PlanetState; n: number; row: number; h: Float32Array; col: Float32Array; colors: Float32Array; heightmap: PlanetState['heightmap']; view: TopoView } | null = null;
  /** corner map: index into CORNER_ZOOMS; above 0 the view follows the viewer */
  private zoomLevel = 0;
  private homeExtent = 0;
  /** a person's height on the mapped planet (world units), from the last update */
  private person = 0;
  private interval = 1;
  /** the height range under the map's window (the base's lowest to highest), for the overlay's density (68.17's follow-up) */
  private range = 0;
  private sprites = new Map<string, HTMLCanvasElement>();

  /**
   * `parent`: mount inside this element (the drone tablet) instead of the HUD's corner; `frame`: a
   * preset frame instead of the area of operations (the tablet frames the operator's reach).
   */
  /** The base's resolution: the terrain is sampled at this many pixels a side and drawn scaled (a big tablet need not sample every pixel). */
  private readonly res: number;

  private readonly budget: number;

  constructor(readonly size = 220, opts: { parent?: HTMLElement; frame?: TopoFrame; baseResolution?: number; budgetMs?: number } = {}) {
    this.corner = !opts.frame;
    this.res = this.corner ? Math.round(size * (1 + CORNER_MARGIN)) : Math.min(size, opts.baseResolution ?? size);   // the corner map's wider window keeps its pixels
    this.budget = opts.budgetMs ?? BUDGET_MS;
    this.el = document.createElement('div');
    this.el.className = 'topo-map';
    this.el.style.cssText = (opts.parent ? `position:relative;width:${size}px;height:${size}px;` : `position:fixed;right:24px;bottom:24px;width:${size}px;height:${size}px;z-index:41;`)
      + 'pointer-events:none;border-radius:8px;overflow:hidden;box-shadow:0 2px 10px rgba(0,0,0,0.55);border:1px solid rgba(255,255,255,0.25);background:#1b2420;';
    if (opts.frame) { this.frame = opts.frame; this.view = { x: 0, y: 0, extent: opts.frame.extent }; this.homeExtent = opts.frame.extent; }
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.canvas.height = size;
    this.el.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d')!;
    this.base = document.createElement('canvas');
    this.base.width = this.base.height = this.res;
    (opts.parent ?? document.body).appendChild(this.el);
  }

  /** Frame the area on the viewer's planet once (objectives and every unit), then keep the base current and draw the overlays. */
  update(state: GameState, viewerTeam: number | null, viewerId: number | null, tick: number, notes?: readonly string[]): void {
    this.notes = notes ?? null;
    const viewer = state.players.find(p => p.id === viewerId) ?? state.players.find(p => p.teamIndex === viewerTeam && p.alive) ?? state.players[0];
    if (!viewer) return;
    const planet = state.planets[this.frame?.planetIndex ?? viewer.planetIndex];
    this.forecast = forecastSeenBy(state, viewerTeam, this.frame?.planetIndex ?? viewer.planetIndex, 4, tick * PHYS_DT);   // 62.19 (d); X18 step 2: the whole forecast with a weather station held
    const cellsOn = !!state.config.weather && !!state.config.weatherCells && !state.config.weatherFixed;
    this.here = this.corner && state.config.weather ? weatherHere(state, viewer.planetIndex, viewer.surfaceDir, tick * PHYS_DT) : null;
    this.arrival = this.corner && cellsOn && this.frame ? arrivalFrom(cellsAt(state, viewer.planetIndex, weatherTime(state, tick * PHYS_DT)), cellsAt(state, viewer.planetIndex, weatherTime(state, tick * PHYS_DT + AHEAD_S)), viewer.surfaceDir, this.frame.north) : null;
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
    this.person = person;
    this.wind = null;
    if (!this.frame) {
      // the area of operations: the objectives and your own side (with no objectives — a duel — everyone, who start near each other)
      const sites = (state.modeState.points?.sites ?? []).filter(s => s.planetIndex === viewer.planetIndex).map(s => s.surfaceDir);
      const side = state.players.filter(p => p.planetIndex === viewer.planetIndex && (sites.length === 0 || p.teamIndex === viewer.teamIndex)).map(p => p.surfaceDir);
      this.frame = topoFrame(viewer.planetIndex, planet.radius, [...sites, ...side], 12 * person);
      this.homeExtent = this.frame.extent;
      if (this.corner) this.zoomLevel = CORNER_ZOOM_START;
      this.view = { x: 0, y: 0, extent: this.homeExtent * (this.corner ? CORNER_ZOOMS[this.zoomLevel] : 1) };
    }
    // the corner map keeps you at its centre at every zoom (owner, Oct 10): the drawn window follows
    // you each frame; the ground is sampled over a wider window, re-sampled once you have drifted far
    if (this.corner && viewer.planetIndex === this.frame.planetIndex) {
      const me = toMap(this.frame, viewer.surfaceDir);
      this.view = { ...this.view!, x: me.x, y: me.y };
    }
    const want = this.corner ? sampleWindow(this.view!, this.sampleView, CORNER_MARGIN) : this.view!;
    this.sampleView = want;
    const sameView = (a: TopoView | null, b: TopoView | null) => !!a && !!b && a.x === b.x && a.y === b.y && a.extent === b.extent;
    const stale = this.builtFor !== planet.heightmap || !sameView(this.baseView, want);
    // a new window starts with a quick coarse pass (a third of the resolution), then refines
    if (stale && (!this.job || this.job.heightmap !== planet.heightmap || !sameView(this.job.view, want))) this.startBase(planet, Math.max(48, Math.round(this.res / 3)));
    if (this.job) this.stepBase(person);
    // 69.57: the wind where the viewer stands (the corner map, the match's wind on), in the map's frame
    if (this.corner && state.config.wind && viewer.planetIndex === this.frame!.planetIndex) {
      const g = (G * planet.mass) / (planet.radius * planet.radius);
      this.wind = windOnMap(this.frame!, viewer.surfaceDir, windAtPerson(state, viewer, tick * PHYS_DT), g);
    }
    this.drawOverlays(state, viewerTeam, viewer, tick, person);
  }

  // --- the base: colour, hillshade, contours --------------------------------

  private startBase(planet: PlanetState, n: number): void {
    const colors = this.job?.heightmap === planet.heightmap ? this.job.colors : computePlanetVertexColors(planet);   // a pan or zoom keeps the colours
    this.job = { planet, n, row: 0, h: new Float32Array(n * n), col: new Float32Array(n * n * 3), colors, heightmap: planet.heightmap, view: { ...(this.sampleView ?? this.view!) } };
  }

  private stepBase(person: number): void {
    const job = this.job!, f = this.frame!, n = job.n, t0 = performance.now(), w = job.view;
    const px = (2 * w.extent) / n;
    for (let first = true; job.row < n && (first || performance.now() - t0 < this.budget); first = false) {
      const j = job.row;
      for (let i = 0; i < n; i++) {
        const dir = fromMap(f, w.x - w.extent + (i + 0.5) * px, w.y + w.extent - (j + 0.5) * px);
        const k = j * n + i;
        job.h[k] = getSurfaceRadius(job.planet, dir) - job.planet.radius;
        const v = nearestSurfaceVertex(job.planet, dir) * 3;
        job.col[k * 3] = job.colors[v]; job.col[k * 3 + 1] = job.colors[v + 1]; job.col[k * 3 + 2] = job.colors[v + 2];
      }
      job.row++;
    }
    if (job.row >= n) this.finishBase(person, px);
  }

  private finishBase(person: number, px: number): void {
    const job = this.job!, n = job.n, h = job.h;
    let lo = Infinity, hi = -Infinity;
    for (const v of h) { lo = Math.min(lo, v); hi = Math.max(hi, v); }
    this.interval = contourInterval(hi - lo, person);
    this.range = hi - lo;
    if (this.base.width !== n) this.base.width = this.base.height = n;
    const img = this.base.getContext('2d')!.createImageData(n, n);
    const at = (i: number, j: number) => h[Math.min(n - 1, Math.max(0, j)) * n + Math.min(n - 1, Math.max(0, i))];
    const lx = -0.6, ly = 0.6, lz = 0.53;    // light from the north-west, 45° up
    for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) {
      const k = j * n + i;
      const dx = (at(i + 1, j) - at(i - 1, j)) / (2 * px), dy = (at(i, j - 1) - at(i, j + 1)) / (2 * px);   // +y is north (up the image)
      const nl = Math.hypot(dx, dy, 1);
      const shade = Math.max(0.45, Math.min(1.25, 0.35 + 0.9 * ((-dx * lx - dy * ly + lz) / nl)));
      const band = Math.floor(h[k] / this.interval);
      const edge = band !== Math.floor(at(i + 1, j) / this.interval) || band !== Math.floor(at(i, j + 1) / this.interval);
      const contour = edge ? (((band % 5) + 5) % 5 === 0 ? 0.5 : 0.72) : 1;
      for (let c = 0; c < 3; c++) img.data[k * 4 + c] = Math.min(255, Math.round(255 * Math.pow(job.col[k * 3 + c], 1 / 2.2) * shade * contour));
      img.data[k * 4 + 3] = 255;
    }
    this.base.getContext('2d')!.putImageData(img, 0, 0);
    this.builtFor = job.heightmap;
    this.baseView = job.view;
    if (n < this.res) this.startBase(job.planet, this.res);   // the coarse pass is up: now the full one (it keeps the colours)
    else this.job = null;
  }

  // --- overlays ---------------------------------------------------------------

  private toPx(dir: Vec3): { x: number; y: number; inside: boolean } {
    const { x, y } = viewToPx(this.view!, this.size, toMap(this.frame!, dir));
    return { x, y, inside: x >= 0 && y >= 0 && x <= this.size && y <= this.size };
  }

  private sprite(kind: TrackMarkerKind, step: number, color: string): HTMLCanvasElement {
    const key = `${kind}:${step}:${color}`;
    let c = this.sprites.get(key);
    if (!c) { c = document.createElement('canvas'); c.width = c.height = 22; drawTrackMarker(c.getContext('2d')!, 22, kind, (step + 0.5) / 5, color); this.sprites.set(key, c); }
    return c;
  }

  private drawOverlays(state: GameState, viewerTeam: number | null, viewer: GameState['players'][number], tick: number, person: number): void {
    const g = this.ctx, n = this.size, f = this.frame!, v = this.view!;
    g.fillStyle = '#1b2420'; g.fillRect(0, 0, n, n);
    if (this.builtFor && this.baseView) {
      // the last sampled ground, where it lies in this view (exact once the view stops moving)
      const b = this.baseView, tl = viewToPx(v, n, { x: b.x - b.extent, y: b.y + b.extent }), w = (2 * b.extent) * n / (2 * v.extent);
      g.imageSmoothingEnabled = true; g.drawImage(this.base, tl.x, tl.y, w, w);
    }
    const pxPerU = n / (2 * v.extent);
    const team = (i: number | null) => (i === null ? '#c9ccd2' : unitPaletteFor(TEAMS[i % TEAMS.length]).body);

    // capture points: the zone as a faint ring in the owner's colour, the capture under way as a fill; on it the point's
    // letter tile — the HUD's top bar's tile (67.35: one grammar)
    const capR = (state.config.modeConfig.captureRadius ?? personSetOf(state).captureRadius) * pxPerU;
    (state.modeState.points?.sites ?? []).forEach((s, idx) => {
      if (s.planetIndex !== f.planetIndex) return;
      const p = this.toPx(s.surfaceDir);
      g.beginPath(); g.arc(p.x, p.y, Math.max(5, capR), 0, Math.PI * 2);
      g.fillStyle = s.owner === null ? 'rgba(220,224,232,0.18)' : hexA(team(s.owner), 0.35); g.fill();
      g.lineWidth = 2; g.strokeStyle = team(s.owner); g.stroke();
      if (s.capturer !== null && s.progress > 0) { g.beginPath(); g.moveTo(p.x, p.y); g.arc(p.x, p.y, Math.max(5, capR), -Math.PI / 2, -Math.PI / 2 + s.progress * Math.PI * 2); g.closePath(); g.fillStyle = hexA(team(s.capturer), 0.45); g.fill(); }
      roundedSquare(g, p.x, p.y, 14, 4); g.fillStyle = s.owner === null ? '#c9ccd2' : TEAMS[s.owner % TEAMS.length].primary; g.fill(); g.lineWidth = 1.2; g.strokeStyle = '#0e1018'; g.stroke();
      g.font = '800 9px system-ui, sans-serif'; g.textAlign = 'center'; g.textBaseline = 'middle';
      g.fillStyle = '#10131a'; g.fillText(String.fromCharCode(65 + idx), p.x, p.y + 0.5);
    });

    // your team's structures: towers ▲, emplacements ◆
    for (const s of state.structures) {
      if (s.planetIndex !== f.planetIndex || s.health <= 0 || (s.type !== 'tower' && s.type !== 'emplacement')) continue;
      const owner = state.players.find(p => p.id === s.ownerId);
      if (!owner || owner.teamIndex !== viewerTeam) continue;
      const p = this.toPx(s.surfaceDir);
      g.beginPath();
      if (s.type === 'tower') { g.moveTo(p.x, p.y - 6); g.lineTo(p.x + 5, p.y + 4); g.lineTo(p.x - 5, p.y + 4); }
      else { g.moveTo(p.x, p.y - 5); g.lineTo(p.x + 5, p.y); g.lineTo(p.x, p.y + 5); g.lineTo(p.x - 5, p.y); }
      g.closePath(); g.fillStyle = team(viewerTeam); g.fill(); g.lineWidth = 1.5; g.strokeStyle = '#0b0b18'; g.stroke();
    }

    // your drones (the tablet's data): the route flown solid, the rest dashed, its waypoints, where the
    // battery will run out, the drone and the ground it sees
    const ps = personSetOf(state);
    for (const d of state.drones ?? []) {
      if (!d.alive || d.teamIndex !== viewerTeam || d.planetIndex !== f.planetIndex) continue;
      const pl = state.planets[d.planetIndex], flash = unitPaletteFor(TEAMS[d.teamIndex % TEAMS.length]).flash;
      const reach = ps.droneSpeed * ps.droneEndurance;
      const total = d.route.slice(1).reduce((a, r, i) => a + Math.acos(Math.max(-1, Math.min(1, dot(norm(d.route[i]), norm(r))))) * pl.radius, 0);
      const end = Math.min(total, reach);
      const line = (from: number, to: number) => {
        g.beginPath();
        const steps = Math.max(2, Math.ceil(((to - from) / (2 * v.extent)) * 48));
        for (let k = 0; k <= steps; k++) { const p = this.toPx(alongRoute(d.route, pl.radius, from + ((to - from) * k) / steps)); if (k) g.lineTo(p.x, p.y); else g.moveTo(p.x, p.y); }
        g.stroke();
      };
      g.lineWidth = 2; g.strokeStyle = hexA(flash, 0.9); line(0, Math.min(d.flown, end));
      g.setLineDash([4, 4]); g.lineWidth = 1.5; g.strokeStyle = hexA(flash, 0.75); line(Math.min(d.flown, end), end); g.setLineDash([]);
      for (const r of d.route.slice(1)) { const p = this.toPx(r); g.beginPath(); g.arc(p.x, p.y, 2.5, 0, Math.PI * 2); g.fillStyle = hexA(flash, 0.9); g.fill(); }
      if (total > reach) { const c = this.toPx(alongRoute(d.route, pl.radius, reach)); g.strokeStyle = '#ff5a4a'; g.lineWidth = 2; g.beginPath(); g.moveTo(c.x - 4, c.y - 4); g.lineTo(c.x + 4, c.y + 4); g.moveTo(c.x + 4, c.y - 4); g.lineTo(c.x - 4, c.y + 4); g.stroke(); }
      const at = dronePos(d, state);
      const p = this.toPx({ x: at.x - pl.position.x, y: at.y - pl.position.y, z: at.z - pl.position.z });
      g.beginPath(); g.arc(p.x, p.y, Math.max(4, ps.droneSightRadius * pxPerU), 0, Math.PI * 2); g.fillStyle = hexA(flash, 0.12); g.fill(); g.lineWidth = 1; g.strokeStyle = hexA(flash, 0.55); g.stroke();
      g.beginPath(); g.arc(p.x, p.y, 4, 0, Math.PI * 2); g.fillStyle = flash; g.fill(); g.lineWidth = 1.5; g.strokeStyle = '#0b0b18'; g.stroke();
    }

    // what your team knows of the enemy (K2: the team picture — shared eyes and instruments, never the truth), fading the same way as the world's markers
    for (const s of viewerTeam === null ? [] : teamPicture(state, viewerTeam, tick)) {
      if (s.planetIndex !== f.planetIndex) continue;
      const c = s.confidence;
      const kind: TrackMarkerKind = s.source === 'mark' ? 'mark' : s.unitId === undefined ? 'dot' : 'seen';
      const p = this.toPx(s.surfaceDir);
      g.drawImage(this.sprite(kind, markerStep(c, 5), kind === 'seen' ? team(s.unitTeam ?? 0) : '#000000'), p.x - 11, p.y - 11);
    }

    // DECISIONS 207 (8): your side's dropships — the wave ship and any called — the table's glyph on a small dark tile in the
    // team's colour, over the ground under it, and while it comes in a dashed line to where it lands
    for (const d of viewerTeam === null ? [] : dropshipMarks(state, viewerTeam)) {
      if (d.planetIndex !== f.planetIndex) continue;
      const p = this.toPx(d.dir), col = TEAMS[d.team % TEAMS.length].primary;
      if (d.to) { const t = this.toPx(d.to); g.setLineDash([3, 3]); g.strokeStyle = hexA(col, 0.8); g.lineWidth = 1.2; g.beginPath(); g.moveTo(p.x, p.y); g.lineTo(t.x, t.y); g.stroke(); g.setLineDash([]); }
      const x = Math.min(n - 9, Math.max(9, p.x)), y = Math.min(n - 9, Math.max(9, p.y));
      roundedSquare(g, x, y, 17, 3); g.fillStyle = 'rgba(14,16,24,0.85)'; g.fill(); g.lineWidth = 1.2; g.strokeStyle = col; g.stroke();
      drawIcon(g, 'dropship', x - 7, y - 7, 14, col);
    }

    // your team: X23's friendly frame, a small rounded square in the team's colour (67.35: the markers' one grammar); you:
    // a white disc with a heading tick (clamped to the edge, pointing, if you have walked off the map) — drawn last, on top
    const mates = state.players.filter(u => u.alive && u.teamIndex === viewerTeam && u.planetIndex === f.planetIndex);
    for (const u of [...mates.filter(u => u.id !== viewer.id), ...mates.filter(u => u.id === viewer.id)]) {
      const p = this.toPx(u.surfaceDir), me = u.id === viewer.id;
      const x = Math.min(n - 4, Math.max(4, p.x)), y = Math.min(n - 4, Math.max(4, p.y));
      if (me) { g.beginPath(); g.arc(x, y, 5, 0, Math.PI * 2); g.fillStyle = '#ffffff'; g.fill(); g.lineWidth = 1.5; g.strokeStyle = '#0e1018'; g.stroke(); }
      else { roundedSquare(g, x, y, 8, 2.5); g.fillStyle = team(u.teamIndex); g.fill(); g.lineWidth = 1; g.strokeStyle = '#0e1018'; g.stroke(); }
      if (me && u.heading) {
        const ahead = this.toPx({ x: u.surfaceDir.x + u.heading.x * 0.02, y: u.surfaceDir.y + u.heading.y * 0.02, z: u.surfaceDir.z + u.heading.z * 0.02 });
        const a = Math.atan2(ahead.y - p.y, ahead.x - p.x);
        g.beginPath(); g.moveTo(x + Math.cos(a) * 6, y + Math.sin(a) * 6); g.lineTo(x + Math.cos(a) * 12, y + Math.sin(a) * 12); g.strokeStyle = '#ffffff'; g.lineWidth = 2; g.stroke();
      }
    }

    // north and scale
    g.fillStyle = 'rgba(11,11,24,0.6)'; g.fillRect(4, 4, 16, 18);
    g.font = '700 10px system-ui, sans-serif'; g.textAlign = 'center'; g.fillStyle = '#e8eaf0'; g.fillText('N', 12, 17);
    g.beginPath(); g.moveTo(12, 5); g.lineTo(15, 9); g.lineTo(9, 9); g.closePath(); g.fill();
    if (this.zoomLevel > 0) { g.fillStyle = 'rgba(11,11,24,0.6)'; g.fillRect(24, 4, 26, 18); g.fillStyle = '#e8eaf0'; g.font = '700 10px system-ui, sans-serif'; g.textAlign = 'center'; g.fillText(`×${Math.round(1 / CORNER_ZOOMS[this.zoomLevel])}`, 37, 17); }
    const bar = scaleBar(v.extent, person), barPx = bar * person * pxPerU;
    g.fillStyle = 'rgba(11,11,24,0.6)'; g.fillRect(4, n - 20, barPx + 50, 16);
    g.fillStyle = '#e8eaf0'; g.fillRect(8, n - 10, barPx, 3);
    g.font = '500 9px system-ui, sans-serif'; g.textAlign = 'left'; g.fillText(`${bar} people`, 12 + barPx, n - 7);
    if (this.wind) this.drawWind(this.wind);
    // 62.19 (d): the forecast, top right — now, then the next segments (minutes on the action clock, rounds on turns)
    // (the owner, Oct 13: one place for the weather — with weather cells the weather here is the cell overhead, the planet's
    // timeline beside it, labelled; without, here is the timeline's now, as before; the front arriving below them)
    const line = (t: string, y: number) => { g.fillStyle = 'rgba(11,11,24,0.7)'; const w = g.measureText(t).width; g.fillRect(n - 8 - w - 3, y - 10, w + 6, 13); g.fillStyle = '#d8dce6'; g.fillText(t, n - 8, y); };
    g.font = '500 9px system-ui, sans-serif'; g.textAlign = 'right';
    if (this.forecast?.length) line(this.here && this.here !== this.forecast[0] ? `here ${this.here} · planet ${this.forecast[0]} → ${this.forecast.slice(1).join(' · ')}` : `${this.forecast[0]} → ${this.forecast.slice(1).join(' · ')}`, 14);
    else if (this.here) line(`here ${this.here}`, 14);
    if (this.arrival) {
      const a = this.arrival, when = a.seconds < 15 ? 'now' : a.seconds < 60 ? `in ${Math.round(a.seconds / 5) * 5} s` : `in ${Math.round(a.seconds / 30) / 2} min`;
      const words: Record<string, string> = { rain: 'rain', storm: 'a storm', fog: 'a fog bank', dust: 'a dust front', ash: 'ash' };
      line(`${words[a.kind] ?? a.kind} ${when}  ${'↑↗→↘↓↙←↖'[Math.round(((a.bearing % 360) + 360) % 360 / 45) % 8]}`, 29);
    }
    // X16: the place's record (the sector's site history, from the host), bottom right, a few lines
    if (this.notes?.length) {
      g.font = '500 9px system-ui, sans-serif'; g.textAlign = 'right';
      const shown = this.notes.slice(0, 3);
      shown.forEach((line, i) => {
        const y = n - 7 - (shown.length - 1 - i) * 11;
        const t = line.length > 60 ? line.slice(0, 58) + '…' : line;
        g.fillStyle = 'rgba(11,11,24,0.7)'; const w = g.measureText(t).width; g.fillRect(n - 8 - w - 3, y - 9, w + 6, 11);
        g.fillStyle = '#d8dce6'; g.fillText(t, n - 8, y);
      });
    }
  }

  /** 69.57: the wind on the map, as `windMark` says (rim: an arrow on the edge it comes from; corner: a chip under the north mark). */
  private drawWind(w: NonNullable<ReturnType<typeof windOnMap>>): void {
    const g = this.ctx, n = this.size, b = (w.bearing * Math.PI) / 180, dx = Math.sin(b), dy = -Math.cos(b);
    const arrow = (x: number, y: number, len: number, width: number) => {
      const hx = x + dx * len, hy = y + dy * len, px = -dy, py = dx;
      g.beginPath(); g.moveTo(x, y); g.lineTo(hx - dx * 4, hy - dy * 4); g.lineWidth = width; g.lineCap = 'round'; g.stroke();
      g.beginPath(); g.moveTo(hx, hy); g.lineTo(hx - dx * 7 + px * 4.5, hy - dy * 7 + py * 4.5); g.lineTo(hx - dx * 7 - px * 4.5, hy - dy * 7 - py * 4.5); g.closePath(); g.fill();
    };
    g.font = '500 9px system-ui, sans-serif';
    if (this.windMark === 'corner') {
      // under the north mark: a plate, the arrow turned in a circle, five pips, the word
      const word = `wind ${w.words}`, wordW = g.measureText(word).width, x0 = 4, y0 = 26, wide = 22 + 5 * 6 + 4 + wordW + 6;
      g.fillStyle = 'rgba(11,11,24,0.6)'; g.fillRect(x0, y0, wide, 18);
      g.strokeStyle = 'rgba(232,234,240,0.35)'; g.lineWidth = 1; g.beginPath(); g.arc(x0 + 10, y0 + 9, 7, 0, Math.PI * 2); g.stroke();
      g.strokeStyle = g.fillStyle = '#e8eaf0';
      if (w.pips > 0) arrow(x0 + 10 - dx * 6, y0 + 9 - dy * 6, 12, 1.6);
      for (let i = 0; i < 5; i++) { g.fillStyle = i < w.pips ? '#e8eaf0' : 'rgba(232,234,240,0.25)'; g.fillRect(x0 + 22 + i * 6, y0 + 5, 4, 8); }
      g.fillStyle = '#d8dce6'; g.textAlign = 'left'; g.fillText(word, x0 + 22 + 5 * 6 + 4, y0 + 13);
      return;
    }
    // the rim: on the edge it comes from (the square's, a margin in), pointing in the way it blows, longer the stronger
    if (w.pips === 0) {   // (calm: the word on the top edge — the map always says what the wind is, DECISIONS 268 (6))
      const word = 'wind calm', ww = g.measureText(word).width;
      g.fillStyle = 'rgba(11,11,24,0.6)'; g.fillRect(n / 2 - ww / 2 - 3, 6, ww + 6, 13);
      g.fillStyle = '#d8dce6'; g.textAlign = 'center'; g.fillText(word, n / 2, 16);
      return;
    }
    // (clear of the map's rows of labels: the north mark and the forecast along the top, the scale along the foot)
    const hx = n / 2 - 14, hy = n / 2 - 30, t = Math.min(hx / Math.max(Math.abs(dx), 1e-9), hy / Math.max(Math.abs(dy), 1e-9)), x = n / 2 - dx * t, y = n / 2 - dy * t;
    const len = 10 + 4 * w.pips;
    g.strokeStyle = 'rgba(11,11,24,0.75)'; g.fillStyle = 'rgba(11,11,24,0.75)'; arrow(x, y, len, 5);   // (a dark edge: it reads on light ground)
    g.strokeStyle = '#e8eaf0'; g.fillStyle = '#e8eaf0'; arrow(x, y, len, 2.2);
    const tx = x + dx * (len + 4), ty = y + dy * (len + 4), word = `wind ${w.words}`;   // (268 (6): says it is the wind)
    g.textAlign = Math.abs(dx) < 0.3 ? 'center' : dx > 0 ? 'left' : 'right';
    const ww = g.measureText(word).width, bx = g.textAlign === 'center' ? tx - ww / 2 : g.textAlign === 'left' ? tx : tx - ww;
    const by = ty + (dy > 0.3 ? 9 : dy < -0.3 ? -2 : 3);
    g.fillStyle = 'rgba(11,11,24,0.6)'; g.fillRect(bx - 2, by - 9, ww + 4, 12);
    g.fillStyle = '#d8dce6'; g.fillText(word, tx, by);
  }

  /** The map's frame (null until the first update), for turning clicks into directions. */
  get mapFrame(): TopoFrame | null { return this.frame; }
  /** The current view (null until framed). */
  get mapView(): TopoView | null { return this.view ? { ...this.view } : null; }

  private limits(): { min: number; max: number } {
    const f = this.frame!;
    return { min: Math.min(this.homeExtent, 8 * (this.person || f.radius * 0.001)), max: f.radius * Math.PI * 0.5 };   // in to a few people across, out to a quarter of the planet
  }
  /** Zoom by `factor` (< 1 in) about pixel (px, py) — the centre if omitted. The tablet's wheel and keys. */
  zoomAt(factor: number, px = this.size / 2, py = this.size / 2): void {
    if (!this.view || !this.frame) return;
    const { min, max } = this.limits();
    this.view = zoomAbout(this.view, factor, px, py, this.size, min, max);
  }
  /** Drag the map by pixels. The tablet's drag. */
  panBy(dx: number, dy: number): void { if (this.view) this.view = panView(this.view, dx, dy, this.size); }
  /** Back to the frame the map opened on. */
  resetView(): void { if (this.frame) { this.zoomLevel = this.corner ? CORNER_ZOOM_START : 0; this.view = { x: 0, y: 0, extent: this.homeExtent * (this.corner ? CORNER_ZOOMS[this.zoomLevel] : 1) }; } }
  /** The corner map's zoom: a step in (+1) or out (−1) through CORNER_ZOOMS; it follows you at every step. */
  zoomStep(dir: number): void {
    if (!this.frame) return;
    this.zoomLevel = Math.max(0, Math.min(CORNER_ZOOMS.length - 1, this.zoomLevel + Math.sign(dir)));
    const ext = this.homeExtent * CORNER_ZOOMS[this.zoomLevel];
    this.view = { x: this.view?.x ?? 0, y: this.view?.y ?? 0, extent: ext };
  }
  /** A direction → this map's pixel, and a pixel → a direction (both through the current view). */
  pixelOf(dir: Vec3): { x: number; y: number } { return viewToPx(this.view!, this.size, toMap(this.frame!, dir)); }
  dirAtPixel(px: number, py: number): Vec3 { const m = pxToView(this.view!, this.size, px, py); return fromMap(this.frame!, m.x, m.y); }

  /** The contour interval in world units (for tests and the sheet). */
  get contourStep(): number { return this.interval; }
  /** The height range under the map's window (0 until it is built). */
  get contourRange(): number { return this.range; }
  /** Whether the base terrain has been drawn. */
  get ready(): boolean { return this.builtFor !== null; }

  dispose(): void { this.el.remove(); }
}

function hexA(hex: string, a: number): string {
  const h = hex.replace('#', '');
  return `rgba(${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)},${a})`;
}

/** A rounded square path of side `size` centred on (x, y): X23's friendly frame and the points' tiles (67.35). */
function roundedSquare(g: CanvasRenderingContext2D, x: number, y: number, size: number, r: number): void {
  const h = size / 2;
  g.beginPath();
  g.moveTo(x - h + r, y - h); g.lineTo(x + h - r, y - h); g.quadraticCurveTo(x + h, y - h, x + h, y - h + r);
  g.lineTo(x + h, y + h - r); g.quadraticCurveTo(x + h, y + h, x + h - r, y + h);
  g.lineTo(x - h + r, y + h); g.quadraticCurveTo(x - h, y + h, x - h, y + h - r);
  g.lineTo(x - h, y - h + r); g.quadraticCurveTo(x - h, y - h, x - h + r, y - h);
  g.closePath();
}
